import React, { useState, useRef, useEffect } from 'react';
import './Header.css';
import { useNavigate } from 'react-router-dom';

export default function Header({ token, userRole, subscription, setView, setUserRole, setToken, setUserEmail, handleLogout }) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownRef = useRef();
  const mobileMenuRef = useRef();
  const hamburgerRef = useRef();

  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  useEffect(() => {
    if (!showMobileMenu) return;
    function handleClick(e) {
      // Ignora cliques no botão hambúrguer
      if (hamburgerRef.current && hamburgerRef.current.contains(e.target)) {
        return;
      }
      // Fecha se clicar fora do menu
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMobileMenu]);

  const handleNavigate = (path) => {
    setShowMobileMenu(false);
    navigate(path);
  };

  return (
    <header className="site-header">
      <div className="header-container">
        {/* Logotipo */}
        <div className="header-brand" onClick={()=>handleNavigate('/')}> 
          <img 
            src="/logotipo_qrcodexpress_white.svg" 
            alt="QRCodeXpress - Fábrica de Código" 
            className="header-logo" 
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          <a href="/" onClick={e=>{e.preventDefault();handleNavigate('/')}} className="nav-link">Home</a>
          <a href="/qrcode-generator" onClick={e=>{e.preventDefault();handleNavigate('/qrcode-generator')}} className="nav-link">Gerar QRCode</a>
          <a href="/planos" onClick={e=>{e.preventDefault();handleNavigate('/planos')}} className="nav-link">Planos</a>
          <a href="/faq" onClick={e=>{e.preventDefault();handleNavigate('/faq')}} className="nav-link">FAQ</a>
          <a href="/contato" onClick={e=>{e.preventDefault();handleNavigate('/contato')}} className="nav-link">Contato</a>
        </nav>

        {/* Desktop Auth Button */}
        <div className="header-actions desktop-actions">
          {!token ? (
            <div className="header-auth-button header-logged-out">
              <button className="btn-primary" onClick={()=>handleNavigate('/connect')}>
                ENTRAR
              </button>
            </div>
          ) : (
            <div className="header-auth-button header-logged-in" ref={dropdownRef}>
              <button className="btn-account" onClick={()=>setShowDropdown(v=>!v)}>
                <div className="account-content">
                  <div style={getBadgeStyle(userRole, subscription)}>
                    {getBadgeIcon(userRole, subscription)} {getBadgeLabel(userRole, subscription)}
                  </div>
                  <span className="account-text">MINHA CONTA</span>
                  <svg className="account-arrow" width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7l5 5 5-5" stroke="#1a73e8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={()=>{setShowDropdown(false);handleNavigate('/minha-conta');}}>
                    📊 Dashboard
                  </button>
                  <button className="dropdown-item" onClick={()=>{setShowDropdown(false);handleLogout();}}>
                    🚪 Sair
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Hamburger Button */}
        <button 
          ref={hamburgerRef}
          className="mobile-menu-toggle" 
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Menu"
        >
          <div className={`hamburger ${showMobileMenu ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="mobile-menu" ref={mobileMenuRef}>
          <nav className="mobile-nav">
            <a href="/" onClick={e=>{e.preventDefault();handleNavigate('/')}} className="mobile-nav-link">
              🏠 Home
            </a>
            <a href="/qrcode-generator" onClick={e=>{e.preventDefault();handleNavigate('/qrcode-generator')}} className="mobile-nav-link">
              📱 Gerar QRCode
            </a>
            <a href="/planos" onClick={e=>{e.preventDefault();handleNavigate('/planos')}} className="mobile-nav-link">
              💎 Planos
            </a>
            <a href="/faq" onClick={e=>{e.preventDefault();handleNavigate('/faq')}} className="mobile-nav-link">
              ❓ FAQ
            </a>
            <a href="/contato" onClick={e=>{e.preventDefault();handleNavigate('/contato')}} className="mobile-nav-link">
              📧 Contato
            </a>
            
            <div className="mobile-divider"></div>
            
            {token ? (
              <>
                <div className="mobile-user-info">
                  <div style={getBadgeStyle(userRole, subscription)}>
                    {getBadgeIcon(userRole, subscription)} {getBadgeLabel(userRole, subscription)}
                  </div>
                </div>
                <a href="/minha-conta" onClick={e=>{e.preventDefault();handleNavigate('/minha-conta')}} className="mobile-nav-link mobile-nav-primary">
                  📊 Minha Conta
                </a>
                <button className="mobile-nav-link mobile-nav-danger" onClick={()=>{setShowMobileMenu(false);handleLogout();}}>
                  🚪 Sair
                </button>
              </>
            ) : (
              <a href="/connect" onClick={e=>{e.preventDefault();handleNavigate('/connect')}} className="mobile-nav-link mobile-nav-primary">
                🔐 Entrar
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

// Funções para o badge profissional
const getBadgeIcon = (role, subscription) => {
  if (role === 'admin') return '👑';
  if (subscription === 'business') return '💼';
  if (subscription === 'pro') return '⭐';
  return '✨';
};

const getBadgeLabel = (role, subscription) => {
  if (role === 'admin') return 'ADMIN';
  if (subscription === 'business') return 'BUSINESS';
  if (subscription === 'pro') return 'PRO';
  return 'FREE';
};

const getBadgeStyle = (role, subscription) => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    boxShadow: '0 2px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
    transition: 'all 0.3s ease',
    border: '2px solid rgba(255,255,255,0.4)',
    position: 'relative',
    overflow: 'hidden'
  };

  // Admin tem prioridade
  if (role === 'admin') {
    return {
      ...baseStyle,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      borderColor: 'rgba(255,255,255,0.5)'
    };
  }

  // Business
  if (subscription === 'business') {
    return {
      ...baseStyle,
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      borderColor: 'rgba(255,255,255,0.5)'
    };
  }

  // Pro
  if (subscription === 'pro') {
    return {
      ...baseStyle,
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      borderColor: 'rgba(255,255,255,0.5)'
    };
  }

  // Free
  return {
    ...baseStyle,
    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    color: '#1a1a1a',
    textShadow: '0 1px 1px rgba(255,255,255,0.8)',
    borderColor: 'rgba(255,255,255,0.6)'
  };
};
